import type { PhysicsTuning } from '../config/tuning';
import { LIVE_BEHIND, type LiveDriver } from '../net/live';
import { decodeInputs } from '../sim/input';
import type { TrackRuntime } from '../sim/trackQuery';
import type { SimEvent } from '../sim/world';
import type { SectorColor } from './session';
import { Spectator } from './spectator';

/**
 * How a friend's car keeps pace with their inputs (ticks): it waits for `fill` of them before playing (at the start,
 * and after running dry); fallen `catchUp` behind it plays two at a time; further than `far` behind (a run joined
 * midway, a tab that slept) it skips ahead unseen, at most `skip` a frame (a long run takes a few frames, not one
 * long stall).
 */
export interface LivePace {
  fill: number;
  catchUp: number;
  far: number;
  skip: number;
}

/** Watching a friend: half a second behind them, so their car never waits for the next batch. */
export const WATCHING: LivePace = { fill: LIVE_BEHIND, catchUp: 75, far: 240, skip: 2500 };
/** Their car beside yours as you race: closer behind them (a third of a second), skipping ahead gently (your own frames come first). */
export const RACING: LivePace = { fill: 12, catchUp: 40, far: 240, skip: 600 };

/** A friend's car as you watch it. */
export interface LiveCar {
  key: string;
  name: string;
  /** Their run (a new one at each restart). */
  run: number;
  paused: boolean;
  /** Their best lap today (what their clock's colours and gaps are against). */
  best: LiveDriver['best'];
  /** Yesterday's winner: their car is gold. */
  gold: boolean;
  sp: Spectator;
  /** Their inputs have begun arriving (until then the car sits on the grid, not shown). */
  fed: boolean;
  /** Waiting for half a second of their driving before playing it (at the start, and after running dry). */
  filling: boolean;
  /** Skipping ahead unseen (a run joined midway), a few frames' worth, until just behind them. */
  skipping: boolean;
  /** This lap's sectors as their clock colours them, and their times. */
  sectors: (SectorColor | null)[];
  sectorMs: (number | null)[];
  /** Their gap to their best at the last sector line (ms), or null. */
  split: number | null;
}

/** Things worth saying about the friend you're following. */
export type LiveNews =
  | { type: 'restart'; car: LiveCar }
  | { type: 'sector'; car: LiveCar }
  | { type: 'lap'; car: LiveCar; ms: number; valid: boolean; sectorMs: number[]; best: number | null }
  | { type: 'invalid'; car: LiveCar }
  | { type: 'barrier'; car: LiveCar; speed: number }
  | { type: 'paused'; car: LiveCar }
  | { type: 'gone'; car: LiveCar };

/**
 * Watching the friends driving in your server right now: each one's car, driven by their inputs as they arrive
 * (Spectator.live), played half a second behind them so the next batch is always here in time. The one you
 * follow (`focus`) has its laps, sectors and troubles told (news); the others drive as ghosts.
 */
export class LiveWatch {
  readonly cars = new Map<string, LiveCar>();
  /** The room's order of drivers (for the chips to switch between them). */
  order: string[] = [];

  constructor(
    private rt: TrackRuntime,
    /** The car for a driver's `day` (`date|track|car`), or null: not this circuit and car (they can't be watched here). */
    private physicsFor: (day: string) => PhysicsTuning | null,
    public focus: string,
    private news: (n: LiveNews) => void = () => {},
    /** Some inputs went missing: ask the drivers for their runs again. */
    private need: () => void = () => {},
    private pace: LivePace = WATCHING,
  ) {}

  get focusCar(): LiveCar | null {
    return this.cars.get(this.focus) ?? null;
  }

  /** The cars shown as ghosts: the others whose driving has arrived. */
  others(): LiveCar[] {
    return this.order.map((k) => this.cars.get(k)).filter((c): c is LiveCar => !!c && c.key !== this.focus && c.fed);
  }

  /** The room's drivers now: newcomers join, those gone leave, a new run starts again from the grid. */
  roster(drivers: readonly LiveDriver[]): void {
    const seen = new Set<string>();
    this.order = [];
    for (const d of drivers) {
      const physics = this.physicsFor(d.day);
      if (!physics) continue;
      seen.add(d.key);
      this.order.push(d.key);
      const car = this.cars.get(d.key);
      if (!car) {
        this.cars.set(d.key, this.newCar(d, physics));
        continue;
      }
      car.name = d.name;
      car.best = d.best;
      car.gold = d.gold;
      if (d.run !== car.run) {
        this.restartCar(car, d.run, physics);
        if (car.key === this.focus) this.news({ type: 'restart', car });
      }
      if (d.paused && !car.paused && car.key === this.focus) this.news({ type: 'paused', car });
      car.paused = d.paused;
    }
    for (const [key, car] of this.cars) {
      if (seen.has(key)) continue;
      this.cars.delete(key);
      if (key === this.focus) this.news({ type: 'gone', car });
    }
  }

  /** A driver's inputs, from tick `from` of their run `run` (as encodeInputs sent them). */
  feed(key: string, run: number, from: number, encoded: string): void {
    const car = this.cars.get(key);
    if (!car || car.run !== run) return;
    let packed: number[];
    try {
      packed = decodeInputs(encoded);
    } catch {
      return;
    }
    if (car.sp.feed(from, packed)) car.fed = true;
    else this.need();
  }

  /** One tick of the simulation: each car plays its next tick once it's here (two if it has fallen behind). */
  step(): void {
    for (const car of this.cars.values()) {
      const sp = car.sp;
      if (car.filling) {
        if (sp.ahead < this.pace.fill) continue;
        car.filling = false;
      }
      // Run dry (a late batch, a pause): the car holds still and waits for half a second's worth again.
      if (sp.ahead <= 0) {
        car.filling = true;
        sp.step();
        continue;
      }
      sp.step();
      if (sp.ahead > this.pace.catchUp) sp.step();
    }
  }

  /**
   * Once a frame: a car far behind (a run joined midway, a tab that slept) skips ahead unseen, a slice a frame, all
   * the way to just behind them (stopping short would leave it trailing, only slowly caught up).
   */
  frame(): void {
    for (const car of this.cars.values()) {
      const sp = car.sp;
      if (sp.ahead > this.pace.far) car.skipping = true;
      if (!car.skipping) continue;
      sp.skipTo(this.pace.fill, this.pace.skip);
      car.filling = false;
      car.skipping = sp.ahead > this.pace.fill;
    }
  }

  /** Still catching up with the one you follow (a long run joined midway): say so rather than show the grid. */
  get joining(): boolean {
    const c = this.focusCar;
    return !c || !c.fed || c.skipping || c.sp.ahead > this.pace.far;
  }

  /** Cars whose driving is here and caught up (not still skipping through a run joined midway). */
  ready(): LiveCar[] {
    return this.others().filter((c) => !c.skipping && c.sp.ahead <= this.pace.far);
  }

  private newCar(d: LiveDriver, physics: PhysicsTuning): LiveCar {
    const car: LiveCar = {
      key: d.key,
      name: d.name,
      run: d.run,
      paused: d.paused,
      best: d.best,
      gold: d.gold,
      sp: Spectator.live(this.rt, physics),
      fed: false,
      filling: true,
      skipping: false,
      sectors: [null, null, null],
      sectorMs: [null, null, null],
      split: null,
    };
    car.sp.onEvents = (events) => this.heard(car, events);
    return car;
  }

  private restartCar(car: LiveCar, run: number, physics: PhysicsTuning): void {
    car.run = run;
    car.sp = Spectator.live(this.rt, physics);
    car.sp.onEvents = (events) => this.heard(car, events);
    car.fed = false;
    car.filling = true;
    car.skipping = false;
    car.sectors = [null, null, null];
    car.sectorMs = [null, null, null];
    car.split = null;
  }

  /** The fastest sector anyone here has driven today (their bests), for purple. */
  private roomBest(k: number): number | null {
    let best: number | null = null;
    for (const c of this.cars.values()) {
      const v = c.best?.sectors[k];
      if (v != null && (best === null || v < best)) best = v;
    }
    return best;
  }

  /** Their clock's colour for a sector: purple (the room's fastest), green (their best), yellow, or invalid. */
  private colour(car: LiveCar, k: number, ms: number): SectorColor {
    if (!car.sp.state.timing.valid) return 'invalid';
    const room = this.roomBest(k);
    if (room === null || ms < room) return 'purple';
    const theirs = car.best?.sectors[k];
    return theirs == null || ms < theirs ? 'green' : 'yellow';
  }

  /** Their gap to their best after sector `k` (the sum of the sectors so far against theirs), or null. */
  private gapAfter(car: LiveCar, k: number): number | null {
    const b = car.best?.sectors;
    if (!b) return null;
    let mine = 0;
    let theirs = 0;
    for (let i = 0; i <= k; i++) {
      const v = car.sectorMs[i];
      if (v == null) return null;
      mine += v;
      theirs += b[i];
    }
    return mine - theirs;
  }

  private heard(car: LiveCar, events: readonly SimEvent[]): void {
    const followed = car.key === this.focus;
    for (const e of events) {
      switch (e.type) {
        case 'lapStart':
          car.sectors = [null, null, null];
          car.sectorMs = [null, null, null];
          car.split = null;
          if (followed) this.news({ type: 'sector', car });
          break;
        case 'sector':
          car.sectorMs[e.sector] = e.ms;
          car.sectors[e.sector] = this.colour(car, e.sector, e.ms);
          car.split = this.gapAfter(car, e.sector);
          if (followed) this.news({ type: 'sector', car });
          break;
        case 'lapComplete': {
          car.sectorMs[2] = e.sectorMs[2];
          car.sectors[2] = e.valid ? this.colour(car, 2, e.sectorMs[2]) : 'invalid';
          car.split = e.valid ? this.gapAfter(car, 2) : null;
          const best = car.best?.ms ?? null;
          if (followed) this.news({ type: 'lap', car, ms: e.ms, valid: e.valid, sectorMs: e.sectorMs, best });
          // A new best: theirs now (the room will say so too, a moment later).
          if (e.valid && (best === null || e.ms < best)) car.best = { ms: e.ms, sectors: e.sectorMs.slice() };
          break;
        }
        case 'invalidated':
          if (followed) this.news({ type: 'invalid', car });
          break;
        case 'barrier':
          if (followed) this.news({ type: 'barrier', car, speed: e.speed });
          break;
      }
    }
  }
}
